import { Component, forwardRef, Input, ChangeDetectorRef, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgForm } from '@angular/forms';
import { SourceCodeExcludesApiService } from '../../services/source-code-excludes-api.service';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { FormUtils } from '@client/ng2/shared/form-utils';
import { safeClone } from '@client/app/shared/utils/object-utils';

interface SourceCodeExcludeForm {
    id?: number;
    module?: string;
    default_for_cms?: string | null;
    [key: string]: any;
}

@Component({
    selector: 'form-source-code-exclude',
    template: `
        <form name="form" #sourceCodeExcludeForm="ngForm" class="form-horizontal" (ngSubmit)="sourceCodeExcludeForm.valid && submit()">
            <div class="mon-dialog-header">
                <page-header class="modal-title" [header]="'Source Code Exclude' | translate"></page-header>
            </div>

            <div class="mon-dialog-body-with-footer">
                <div *ngIf="step === 0">
                    <form-source-code-exclude-steps-module
                        (selectModule)="onSelectModule($event)">
                    </form-source-code-exclude-steps-module>
                </div>

                <div *ngIf="step === 1">
                    <form-source-code-exclude-steps-form [model]="ngModel"></form-source-code-exclude-steps-form>
                </div>
            </div>

            <div class="mon-dialog-footer d-flex align-items-start">
                <div class="pl-4" *ngIf="step === 1">
                    <span class="text-link fas fa-info-circle" aria-hidden="true"></span>
                    <span translate>Remember to add this source code exclude to your domain(s) through the Edit Domain screen.</span>
                </div>
                <div class="align-items-end ml-auto">
                    <button class="btn btn-border-radius btn-secondary mr-2"
                            *ngIf="step === 1 && !ngModel.id"
                            type="button"
                            (click)="previousStep()"
                            translate>Back</button>
                    <button data-test="save-button"
                            class="btn btn-border-radius emphasised-btn"
                            *ngIf="step === 1"
                            [disabled]="saving || sourceCodeExcludeForm.invalid" type="submit">
                        <span *ngIf="!saving" translate>Save</span>
                        <span *ngIf="saving"
                              class="fa-spin fa-fw"
                              [ngClass]="['SPINNER', 'ICON'] | monIcons"
                              aria-hidden="true">
                        </span>
                    </button>
                </div>
            </div>
        </form>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormSourceCodeExcludeComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class FormSourceCodeExcludeComponent implements ControlValueAccessor, OnInit, AfterViewInit {
    @Input() sourceCodeExclude?: any;
    @Input() module?: string;
    @Input() monDialogId?: string;
    @Input() setForm: any;
    @ViewChild('sourceCodeExcludeForm') sourceCodeExcludeForm?: NgForm;

    step = 0;
    ngModel: SourceCodeExcludeForm = {};
    saving = false;

    private onChange = (value: any) => {};
    private onTouched = () => {};

    constructor (
        private sourceCodeExcludesApiService: SourceCodeExcludesApiService,
        private monDialog: MonDialog,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngAfterViewInit () {
        this.monDialog.setForm(this.monDialogId, this.sourceCodeExcludeForm);
    }

    writeValue (value: any): void {
        if (value !== undefined) {
            this.ngModel = value;
            this.cdr.detectChanges();
        }
    }

    registerOnChange (fn: (value: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched (fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState (isDisabled: boolean): void {
        // Handle disabled state if needed
    }

    private activate (): void {
        this.step = 0;
        this.ngModel = safeClone(this.sourceCodeExclude || {});
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.ngModel.module = this.module!;
        this.setupStep();
    }

    selectModule (module: string): void {
        this.ngModel.module = module;
        this.nextStep();
    }

    onSelectModule (event: { module: string }): void {
        this.selectModule(event.module);
    }

    nextStep (): void {
        this.step++;
    }

    previousStep (): void {
        this.step--;
    }

    submit (): void {
        let promise: Promise<any>;
        this.ngModel.default_for_cms = this.ngModel.default_for_cms || null;
        const model = safeClone(this.ngModel);
        this.saving = true;

        if (isNaN(parseFloat(String(this.ngModel.id)))) {
            if (this.ngModel.module) {
                promise = this.sourceCodeExcludesApiService.create(this.ngModel.module, model);
            } else {
                this.saving = false;
                return;
            }
        } else {
            if (this.ngModel.module) {
                promise = this.sourceCodeExcludesApiService.update(
                    this.ngModel.module,
                    this.sourceCodeExclude,
                    model,
                );
            } else {
                this.saving = false;
                return;
            }
        }

        promise.then(
            (sourceCodeExclude) => {
                FormUtils.markFormAsPristine(this.sourceCodeExcludeForm);
                this.close(sourceCodeExclude);
            },
            () => {
                this.saving = false;
            },
        );
    }

    close (sourceCodeExclude?: any): void {
        // In the original AngularJS version, this would call monDialog.close()
        // For Angular, we'll emit the value and let the parent handle closing
        this.onChange(sourceCodeExclude);
        this.onTouched();

        // If we have a modal service available, we could close the modal here
        // For now, we'll just emit the change
        this.monDialog.close(this.monDialogId, sourceCodeExclude);
    }

    private setupStep (): void {
        if (this.ngModel.id && this.ngModel.id > 0) {
            this.step = 1;
        }
    }
}
